import Communications from './communications.service';
import queryString from "query-string";
import {FormikErrors} from "formik";
import axios, {CancelTokenSource} from "axios";
import ApiService from './api.service';
import moment, {Moment} from "moment";
import AlertService from "./alert.service";
import RouteConfigService from "./route-config.service";
import AccountService from "./modules/account.service";
import LocalStorageService from "./local-storage.service";
import {v4 as uuidv4} from 'uuid';
import UserService from "./modules/user.service";
import StaticDataService from "./modules/static-data.service";
import * as yup from "yup";
import {IConfirmationConfig} from "../models/confirmation.model";
import AccessioningService from "./modules/accessioning.service";
import LocationService from './modules/location.service';
import FacilityService from './modules/facility.service';
import TestService from './modules/test.service';
import resultService from "./modules/result.service";
import TestOrderService from './modules/test-order.service';
import PatientService from "./modules/patient.service";
import LabService from "./modules/lab.service";
import QrCodeService from "./modules/qrcode.service";
import {saveAs} from 'file-saver';
import BarcodeService from "./modules/barcode.service";
import SystemSettingsService from "./modules/system-settings";
import DashboardService from "./modules/dashboard.service";
import AccessLevelService from "./modules/access.service";
import InventoryService from "./modules/inventory.service";
import LabReportGeneratorService from "./lab-report-generator.service";
import BiomarkerService from "./modules/biomarker.service";
import TicketService from "./modules/ticket.service";
import {ImageConfig} from "../../constants";
import {SystemConfigTypes} from "../../store/reducers/staticData.reducer";
import {medicationObject} from "../models/testOrder.model";
import {LabRoleType} from "../models/lab.model";

yup.addMethod(yup.mixed, 'atLeastOne', (args) => {
    const {message} = args;
    // @ts-ignore
    return this.test('atLeastOne', message, (list: any) => {
        // If there are 2+ elements after filtering, we know atMostOne must be false.
        return Object.keys(list).filter(item => item).length > 0
    })
});

const CurrentDate: Moment = moment();

const parseQueryString = (q: string): any => {
    return queryString.parse(q.replace('?', ''));
}

const getBytesInMB = (bytes: number) => {
    return bytes / (1024 * 1024);
}

const formatSizeUnits = (bytes: number, decimals = 2) => {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const getRandomID = (length: number) => {
    let text = '';
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
}

const getUUID = () => {
    return uuidv4();
}

const handleErrors = (setErrors: (errors: FormikErrors<any>) => void, err: any) => {
    if (err.errors) {
        const errors: any = {};
        for (let field in err.errors) {
            if (err.errors.hasOwnProperty(field)) {
                errors[field] = err.errors[field][0];
            }
        }
        setErrors(errors);
    } else if (err.error) {
        AlertService.showToast(err.error);
    }
}

const openDialog = (component: any) => {
    return new Promise((resolve, reject) => {
        Communications.DialogStateSubject.next({component, promise: {resolve, reject}})
    });
}

const getCancelToken = (): CancelTokenSource => {
    return axios.CancelToken.source();
}

const getPayloadFilterDates = (mode: 'day' | 'week' | 'month' | 'year'): { start_date: string, end_date: string } => {
    const payload = {start_date: '', end_date: moment().endOf('day').format('MM-DD-YYYY')}
    switch (mode) {
        case "day":
            payload.start_date = moment().startOf('day').format('MM-DD-YYYY')
            break;
        case "week":
            payload.start_date = moment().subtract(1, 'week').startOf('day').format('MM-DD-YYYY')
            break;
        case "month":
            payload.start_date = moment().subtract(1, "month").startOf('day').format('MM-DD-YYYY')
            break;
        case "year":
            payload.start_date = moment().subtract(1, "year").startOf('day').format('MM-DD-YYYY')
            break;
    }
    return payload;
}

const convertDateFormat = (date: Date, format: string = 'MM-DD-YYYY') => {
    return moment(date).format(format);
}

const getFlatJsonFromNestedJSON = (jsonData: any, rootName: string = "", ignoreList: any[] = []): any => {

    const appendFormData = (data: any, root: any) => {
        let newObj: any = {};
        let tmp: any = {};
        if (!ignore(root)) {
            root = root || '';
            if (data instanceof File) {
                newObj[root] = data;
            } else if (Array.isArray(data)) {
                for (let i = 0; i < data.length; i++) {
                    tmp = appendFormData(data[i], root + '[' + i + ']');
                    newObj = {...newObj, ...tmp}
                }
            } else if (typeof data === 'object' && data) {
                for (let key in data) {
                    if (data.hasOwnProperty(key)) {
                        if (root === '') {
                            tmp = appendFormData(data[key], key);
                            newObj = {...newObj, ...tmp}
                        } else {
                            tmp = appendFormData(data[key], root + '.' + key);
                            newObj = {...newObj, ...tmp}
                        }
                    }
                }
            } else {
                if (data !== null && typeof data !== 'undefined') {
                    newObj[root] = data;
                }
            }
        }
        return newObj;
    }

    const ignore = (root: any) => {
        return Array.isArray(ignoreList)
            && ignoreList.some((x) => {
                return x === root;
            });
    }

    return appendFormData(jsonData, rootName);
};

const getFormDataFromJSON = (obj: any, rootName = '', ignoreList = []): FormData => {

    const formData = new FormData();

    const appendFormData = (data: any, root: any) => {
        if (!ignore(root)) {
            root = root || '';
            if (data instanceof File) {
                formData.append(root, data);
            } else if (Array.isArray(data)) {
                for (let i = 0; i < data.length; i++) {
                    appendFormData(data[i], root + '[' + i + ']');
                }
            } else if (typeof data === 'object' && data) {
                for (let key in data) {
                    if (data.hasOwnProperty(key)) {
                        if (root === '') {
                            appendFormData(data[key], key);
                        } else {
                            appendFormData(data[key], root + '.' + key);
                        }
                    }
                }
            } else {
                if (data !== null && typeof data !== 'undefined') {
                    formData.append(root, data);
                }
            }
        }
    }

    const ignore = (root: any) => {
        return Array.isArray(ignoreList)
            && ignoreList.some((x: any) => {
                return x === root;
            });
    }

    appendFormData(obj, rootName);

    return formData;

}
const capitalizeFirstLetter = (string: string) => {
    if (typeof string !== 'string') return string;
    if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    return "";
}

const capitalizeOnlyFirstLetter = (string: string) => {
    if (typeof string !== 'string') return string;
    if (string) {
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
    }
    return "";
}
const capitalizeFirstLetterOfEachWord = (string: string) => {
    if (typeof string !== 'string') return string;
    return string?.split(' ').map((word: string) => capitalizeFirstLetter(word)).join(' ');
}
const capitalize = (string: string) => {
    if (typeof string !== 'string') return string;

    return string?.toUpperCase();
}

const capitalizeOnlyFirstLetterOfEachWord = (string: string) => {
    if (typeof string !== 'string') return string;
    return string?.split(' ').map((word: string) => capitalizeOnlyFirstLetter(word)).join(' ');
}

const mapLabTypeToName = (type: string) => {
    let labTitle = '';
    StaticDataService.labType.forEach((labType: any) => {
        if (labType.code === type) {
            labTitle = labType.title;
        }
    });
    return labTitle;
}
const mapLabRoleToName = (type: LabRoleType) => {
    return StaticDataService.labRoles.find((roleObj) => roleObj.code === type)?.title;
}

const kFormatter = (num: any) => {
    // @ts-ignore
    return Math.abs(num) > 999 ? Math.sign(num) * ((Math.abs(num) / 1000).toFixed(1)) + 'k' : Math.sign(num) * Math.abs(num)
}

const getLettersFromNumber = (number: number) => {
    const previousLetters: any = (number >= 26 ? getLettersFromNumber(Math.floor(number / 26) - 1) : '');
    const lastLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[number % 26];
    return previousLetters + lastLetter;
}

const getRandomNumberBetweenRange = (startingNumber: number, endingNumber: number) => {
    startingNumber = Math.ceil(startingNumber);
    endingNumber = Math.floor(endingNumber);
    return Math.floor(Math.random() * (endingNumber - startingNumber + 1)) + startingNumber;
}

const getMinsAndSecondsFromSeconds = (numberOfSeconds: number) => {
    const minutes = Math.floor(numberOfSeconds / 60);
    const seconds = (numberOfSeconds % 60);
    return {
        minutes: minutes < 10 ? '0' + minutes : minutes,
        seconds: seconds < 10 ? '0' + seconds : seconds
    }
}

const downloadFile = (url: string, fileName: any, type = 'pdf') => {
    switch (type) {
        case 'image':
            axios.get(url, {
                headers: {'Content-Type': 'image/*'}
            }).then(
                (data: any) => {
                    const blob = new Blob([data.data], {
                        type: 'image/*' // must match the Accept type
                    });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                }, (error) => {
                });
            break;
        case 'image/png':
            (async () => {
                let blob = await fetch(url).then((r) => r.blob());
                saveAs(blob, fileName);
            })();
            // ApiService.get(url, {},{
            //     'Content-Type': 'application/octet-stream'
            // }).then(
            //     (data: any) => {
            //         const blob = new Blob([data], {
            //             type: 'image/png' // must match the Accept type
            //         });
            //         console.log(blob);
            //         const link = document.createElement('a');
            //         link.href = window.URL.createObjectURL(blob);
            //         link.download = fileName;
            //         console.log(link);
            //         link.click();
            //     }, (error) => {
            //         console.log('error');
            //     });
            break;
        case 'pdf':
            axios.get(url, {
                headers: {Accept: 'application/pdf'}, responseType: 'blob'
            }).then(
                (data: any) => {
                    const blob = new Blob([data], {
                        type: 'application/pdf' // must match the Accept type
                    });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(link.href);
                }, (error) => {
                });
            break;
        case 'csv':
            axios.get(url, {
                headers: {Accept: 'application/csv'}, responseType: 'blob'
            }).then(
                (response: any) => {
                    const blob = new Blob([response.data], {
                        type: "application/octet-stream" // or Content-type: "application/vnd.ms-excel"
                    });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(link.href);
                }, (error) => {
                });
            break;
        case 'xlsx':
            axios.get(url, {
                headers: {Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'},
                responseType: 'blob'
            }).then(
                (response: any) => {
                    const blob = new Blob([response.data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(link.href);
                }, (error) => {
                });
            break;
        default:
            console.log(type, ' file download not supported');
    }
}

const generateBlobFileFromUrl = (attachmentUrl: string, attachmentTitle: string, attachmentType: string): Promise<File> => {
    return fetch(attachmentUrl)
        .then((res) => res.blob())
        .then((myBlob) => {
            const myFile = new File([myBlob], attachmentTitle, {
                type: attachmentType
            });
            return myFile;
        });
};
const getFlatQuestionList = (stepData: any): any => {
    const flatQuestionList: any = [];
    const questionListParser = (arrayData: []) => {
        arrayData.forEach((option: any, index, array) => {
            if (option?.questions) {
                option?.questions.forEach((question: any) => {
                    flatQuestionList.push(question);
                    if (question?.options) {
                        questionListParser(question?.options);
                    }
                })
            }
        })
    }
    if (stepData?.questions) {
        stepData?.questions.forEach((value: any) => {
            flatQuestionList.push(value);
            if (value?.options) {
                questionListParser(value?.options);
            }
        })
    }
    return flatQuestionList;

};

const getArrayOfValuesOfOneKeyFromJSON = (array: any[], key: string) => {
    if (array && array.length > 0) {
        const arrayWithDesiredKeyValue = array.map((item) => {
            return item[key];
        });
        return arrayWithDesiredKeyValue;
    } else {
        return [];
    }
}

const openConfirmationDialog = (config: IConfirmationConfig = {}) => {
    const defaultConfig: IConfirmationConfig = {
        direction: "down",
        yes: {text: 'Yes, Confirm', color: 'primary'},
        no: {text: 'No, Cancel', color: 'primary'},
    }
    config = {...defaultConfig, ...config};
    return new Promise((resolve, reject) => {
        Communications.ConfirmStateSubject.next({config, promise: {resolve, reject}})
    });
}

const Capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

// const createValidationsObject = (questions: any) => {
//
//     const generateValidationsForQuestion = (question: IIntakeFormQuestion, parentQuestion: any = null, parentOption: any = null) => {
//         if (question?.isRequired) {
//             question.validationType = 'mixed';
//             const validationsObj: any = {};
//             question.validations = [];
//             if (parentQuestion && parentOption) {
//                 validationsObj.parent = parentQuestion.name + '.' + parentOption.code;
//                 validationsObj.parentParams = [];
//                 if (parentQuestion?.isRequired) {
//                     const parentValidationsObj = {
//                         type: 'when', params: [validationsObj.parent, {
//                             is: true, then: yup[question.validationType]()["required"]('Required'),
//                             otherwise: yup[question.validationType]().nullable()
//                         }]
//                     }
//                     question.validations.push(parentValidationsObj);
//                     if (parentQuestion.formControlType === "RadioButton") {
//                         validationsObj.parent = parentQuestion.name;
//                         const vobj = {
//                             type: 'when', params: [validationsObj.parent, {
//                                 is: parentOption.code, then: yup[question.validationType]()["required"]('Required'),
//                                 otherwise: yup[question.validationType]().nullable()
//                             }]
//                         }
//                         question.validations.push(vobj);
//                     }
//                 }
//             } else {
//                 validationsObj.type = "required";
//                 validationsObj.params = ["Required"];
//                 question.validations.push(validationsObj);
//             }
//             if (question.formControlType === "CheckBox") {
//                 validationsObj.type = "test";
//                 validationsObj.params = ['atLeastOne', 'Select at least one item', (answers: any) => {
//                     return answers ? Object.values(answers).some(answer => answer === true) : false;
//                 }];
//                 question.validations.push(validationsObj);
//             }
//         }
//         if (question.options) {
//             question.options.forEach((option: any) => {
//                 if (option.questions) {
//                     option.questions.forEach((subQuestion: any) => {
//                         if (!question.validations) {
//                             question.validations = [];
//                         }
//                         question.validations.push(generateValidationsForQuestion(subQuestion, question, option));
//                     })
//                 }
//             });
//         }
//         return question;
//     };
//     return questions?.map((question: any) => {
//         return generateValidationsForQuestion(question);
//     });
// }

// const createYupSchema = (mainSchema: any, config: any) => {
//
//     const generateYupSchema = (question: IIntakeFormQuestion) => {
//         const {name, validationType, options, validations = []} = question;
//         if (!yup[validationType]) {
//             return;
//         }
//         let validator: any = yup[validationType]();
//         validations.forEach((validation: any) => {
//             const {params, type} = validation;
//             if (!validator[type]) {
//                 return;
//             }
//             validator = validator[type](...params);
//         });
//         mainSchema[name] = validator;
//         if (options) {
//             options?.forEach((option: any) => {
//                 if (option?.questions) {
//                     option?.questions?.forEach((subQuestion: any) => {
//                         console.log({subQuestion});
//                         generateYupSchema(subQuestion);
//                     })
//                 }
//             });
//         }
//     }
//     generateYupSchema(config);
//     return mainSchema;
// }

const getTypeOfaVariable = (variable: any) => {
    return Object.prototype.toString.call(variable).slice(8, -1).toLowerCase();
}

const setQueryParam = (key: string, value: string, announce: boolean = true) => {
    const existingUrl = new URL(window.location.href);
    let existingUrlString = existingUrl.toString();
    let updatedURL = '';
    const re = new RegExp("([?&])" + key + "=.*?(&|#|$)", "i");
    if (value === undefined) {
        if (existingUrlString.match(re)) {
            updatedURL = existingUrlString.replace(re, '$1$2').replace(/[?&]$/, '').replaceAll(/([?&])&+/g, '$1').replace(/[?&]#/, '#');
        } else {
            updatedURL = existingUrlString;
        }
    } else {
        if (existingUrlString.match(re)) {
            updatedURL = existingUrlString.replace(re, '$1' + key + "=" + value + '$2');
        } else {
            let hash = '';
            if (existingUrlString.indexOf('#') !== -1) {
                hash = existingUrlString.replace(/.*#/, '#');
                existingUrlString = existingUrlString.replace(/#.*/, '');
            }
            const separator = existingUrlString.indexOf('?') !== -1 ? "&" : "?";
            updatedURL = existingUrlString + separator + key + "=" + value + hash;
        }
    }
    if (announce) {
        CommonService._communications.URLQueryParamsDataSubject.next(updatedURL);
    }
    window.history.pushState({}, '', updatedURL);
}

const setUrlSearchParams = (searchParams: {
    [key: string]: string | number;
}) => {
    const queryParams = new URLSearchParams(searchParams as Record<string, string>).toString();
    return `?${queryParams.replace("null", '').replace("undefined", "")}`;
};

const removeQueryParam = (key: string, announce: boolean = true) => {
    const existingUrl = new URL(window.location.href);
    existingUrl.searchParams.delete(key);
    const url = existingUrl.toString();
    if (announce) {
        CommonService._communications.URLQueryParamsDataSubject.next(url);
    }
    window.history.pushState({}, '', url);
}

const mapFilterDataToQueryParams = (filterData: any, excludeKeys: string[] = []) => {
    // CommonService._communications.URLQueryParamsDataSubject.next(undefined);
    Object.keys(filterData).forEach((key: string) => {
        const value = filterData[key];
        if (!excludeKeys.includes(key) && value && value !== '' && value?.length > 0) {
            CommonService.setQueryParam(key, value, false);
        } else {
            removeQueryParam(key, false);
        }
    });
    const existingUrl = new URL(window.location.href);
    let existingUrlString = existingUrl.toString();
    CommonService._communications.URLQueryParamsDataSubject.next(existingUrlString);
};

const getAppliedFiltersCount = (filterData: any, excludeKeys: string[] = []) => {
    let count = 0;
    Object.keys(filterData).forEach((key: string) => {
        const value = filterData[key];
        if (!excludeKeys.includes(key) && value && value !== '' && value?.length > 0 && key !== 'search') {
            const type = getTypeOfaVariable(value);
            if (type === 'array') {
                count += value.length;
            } else {
                count += 1;
            }
        }
    });
    return count;
};

const getMaskedMobileNumber = (mobileNumber: string) => {
    if (mobileNumber && mobileNumber.length === 10) {
        const x = mobileNumber.replace(/\D/g, '').match(/(\d{3})(\d{3})(\d{4})/);
        // @ts-ignore
        return !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
    }
    return mobileNumber;
}

function isSpecificModuleToHide(module: string){
    //commented out lab os route from menu list
    const specificModules = ['Accession','AccessControl','SystemSettings']
    return specificModules.includes(module);
}

/**
 * Dummy function to hide specific features in the UI
 */
function isReadOnlyFeature(){
    return false;
}

/**
 * Checks if any of the provided feature codes are for add, edit, or delete actions.
 * By convention, these action codes contain the keywords 'add', 'edit', 'toggle' or 'delete'.
 *
 * @param {...string} features - A list of feature codes to check.
 * @returns {boolean} - Returns true if any feature code contains 'add', 'edit', 'toggle' or 'delete', otherwise returns false.
 */
function isMutationFeature(...features: string[]) {
    const specificFeatures = ['testorderresultcorrection','samplesendout','paneluploadxls'];
  // Keywords that signify a mutation (a change to data)
  const mutationKeywords = ['add', 'edit', 'delete', 'toggle', ...specificFeatures];

  // The .some() method checks if at least one element in an array passes a test.
  // It returns true as soon as it finds a match, which is very efficient.
  const hasMutation = features.some(featureCode => {
    // Convert to lower case for a case-insensitive search
    const lowerCaseFeature = featureCode.toLowerCase();

    // Check if the feature code includes any of our mutation keywords
    return mutationKeywords.some(keyword => lowerCaseFeature.includes(keyword));
  });

  // The requirement is to return 'true' if a mutation feature is found.
  // So we return the opposite of our 'hasMutation' result.
  return hasMutation;
}

const checkAccess = (module: string, feature: string, userAccess: any) => {
    if (isMutationFeature(feature)) {
        return false;
    }
    let featureAccess: any = [];
    if (!userAccess) {
        return false;
    }
    Object.keys(userAccess)?.map((key) => {
        if (key === module) {
            featureAccess = userAccess[key];
        }
        return key;
    });
    let canAccess = featureAccess?.map((featureObject: any) => featureObject?.feature).includes(feature);
    return canAccess;
}

const formatDate = (date: any, format: string = 'MM/DD/YYYY') => {
    if (!date || !moment(date, true).isValid()) {
        return "N/A";
    }
    return moment(date).format(format);
}
const formatTime = (date: any, format: string = 'hh:mm A') => {
    return moment(date).format(format);
}

function convertTo12HourFormat(timeStr: string, format: string = "hh:mm A") {
    return moment(timeStr, "HH:mm").format(format);
}

const rgbaToHex = (rgba: { r: string; g: string; b: string; a: string }): string => {
    const {r, g, b, a} = rgba;

    // Convert the RGBA values to integers
    const red = parseInt(r, 10);
    const green = parseInt(g, 10);
    const blue = parseInt(b, 10);
    const alpha = parseFloat(a);

    // Convert each component to a two-digit hexadecimal representation
    const hexRed = red.toString(16).padStart(2, "0");
    const hexGreen = green.toString(16).padStart(2, "0");
    const hexBlue = blue.toString(16).padStart(2, "0");
    const hexAlpha = Math.round(alpha * 255).toString(16).padStart(2, "0");

    // Combine the components to form the hexadecimal color code
    const hexCode = `#${hexRed}${hexGreen}${hexBlue}${hexAlpha}`;

    return hexCode;
};

const hexToRgba = (hexCode: string): { r: string; g: string; b: string; a: string } => {
    // Remove the "#" character if present
    if (!hexCode) {
        return {
            r: '241',
            g: '112',
            b: '19',
            a: '1',
        }
    }
    const hex = hexCode.replace("#", "");

    // Extract the individual components
    const red = parseInt(hex.substr(0, 2), 16);
    const green = parseInt(hex.substr(2, 2), 16);
    const blue = parseInt(hex.substr(4, 2), 16);
    const alpha = parseInt(hex.substr(6, 2), 16) / 255;

    // Return the RGBA values as an object
    return {r: red.toString(), g: green.toString(), b: blue.toString(), a: alpha.toString()};
};

const codeToTitleConverter = (code: string) => {
    const words = code.split(/(?=[A-Z])/);
    const capitalizedWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    return capitalizedWords.join(' ');
}

const getFullName = (nameObject: any) => {
    let fullName = '';
    if (nameObject?.firstName) {
        fullName = nameObject?.firstName;
    }
    if (nameObject?.middleName) {
        fullName = fullName + ' ' + nameObject?.middleName;
    }
    if (nameObject?.lastName) {
        fullName = fullName + ' ' + nameObject?.lastName;
    }
    return capitalizeFirstLetterOfEachWord(fullName);
}

const generateRuleFromRuleObject = (ruleObject: any) => {
    let value1 = ruleObject?.value1;
    let value2 = ruleObject?.value2;
    let expression = ruleObject?.expression;
    let units = ruleObject?.units;
    let result = ruleObject?.result;
    let rule = '';
    if (value1) {
        rule = rule + value1;
    }
    if (expression) {
        rule = rule + expression;
    }
    if (value2) {
        rule = rule + value2;
    }
    if (units) {
        rule = rule + ' | ' + units;
    }
    if (result) {
        rule = rule + ' | ' + capitalizeFirstLetterOfEachWord(result);
    }
    return rule;
}
const extract24hTimeFromDate = (date: Date) => {

// Get the current hours and minutes
    let hours: number | string = date.getHours();
    let minutes: number | string = date.getMinutes();

// Ensure hours and minutes are two digits
    if (hours < 10) {
        hours = '0' + hours;
    }
    if (minutes < 10) {
        minutes = '0' + minutes;
    }

// Combine hours and minutes into the "HH:MM" format
    const time: string = `${hours}:${minutes}`;
    return time;// example 23:20

}

const generateNameFromNameObject = (nameObject: any) => {
    let fullName = '';
    if (nameObject?.firstName) {
        fullName = nameObject?.firstName;
    }
    if (nameObject?.middleName) {
        fullName = fullName + ' ' + nameObject?.middleName;
    }
    if (nameObject?.lastName) {
        fullName = fullName + ' ' + nameObject?.lastName;
    }

    return capitalizeFirstLetterOfEachWord(fullName);
}

const getFilteredUsersDropdownList = (selectedUserList: any[], allUserList: any[]) => {
    return allUserList.filter((user: any) => !selectedUserList.find((selectedUser: any) => selectedUser?.id === user?.id));
}

type AnyObject = { [key: string]: any };

const removeEmptyValuesFromObject = (obj: AnyObject, ignoreKeys: string[] = []): AnyObject => {
    const isEmptyValue = (value: any): boolean => {
        return (value === null ||
            value === undefined ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0) || (typeof value === 'object' && true && Object.keys(value).length === 0));
    };

    const cleanObject = (input: AnyObject): AnyObject => {
        return Object.keys(input).reduce((acc: AnyObject, key: string) => {
            if (ignoreKeys.includes(key)) {
                acc[key] = input[key];
            } else if (typeof input[key] === 'object' && input[key] !== null && !Array.isArray(input[key])) {
                const nestedObject = cleanObject(input[key]);
                if (!isEmptyValue(nestedObject)) {
                    acc[key] = nestedObject;
                }
            } else if (!isEmptyValue(input[key])) {
                acc[key] = input[key];
            }
            return acc;
        }, {});
    };

    return cleanObject(obj);
}

const convertJsonToCsv = (jsonData: any, returnType: "blob" | "file", downloadFileName?: string): File | Blob | undefined => {
    try {
        // Convert JSON to CSV
        const jsonKeys = Object.keys(jsonData);
        // Wrap headers in double quotes to prevent commas from splitting
        const headers = jsonKeys.map(key => `"${key}"`).join(',');

        const csvRows = Object.keys(jsonData[jsonKeys[0]]).map(index =>
            jsonKeys.map(key => jsonData[key][index]).join(',')
        ).join('\n');

        const csvContent = headers + '\n' + csvRows;
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});

        if (downloadFileName) {
            // Create a download link and trigger download
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${downloadFileName}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }

        if (returnType === "file") {
            return new File([blob], `${downloadFileName}.csv`, {type: 'text/csv'});
        } else if (returnType === "blob") {
            return blob;
        }

    } catch (error) {
        console.error('Error converting JSON to CSV:', error);
        return undefined;
    }
};

const extractCollectionDevicesFromSampleType = (sampleType: string | string[], sampleTypeList: any[]) => {
    try {
        // Ensure sampleType is an array
        const sampleTypes = Array.isArray(sampleType) ? sampleType : [sampleType];

        // Create a set to store unique collection devices
        const collectionDevicesSet = new Set();

        // Iterate over each sample type
        sampleTypes.forEach((type) => {
            const selectedSampleType = sampleTypeList?.find((sampleItem) => sampleItem?.sampleType === type);
            if (selectedSampleType?.sampleCollectionDeviceName) {
                selectedSampleType.sampleCollectionDeviceName.forEach((device: any) => {
                    collectionDevicesSet.add(JSON.stringify(device));
                });
            }
        });

        // Convert set to array of objects
        const uniqueCollectionDevices = Array.from(collectionDevicesSet).map((device: any) => JSON.parse(device));

        return uniqueCollectionDevices;
    } catch (error) {
        console.error('Error extracting collection devices from sample type:', error);
        return [];
    }
};
const sortPatientInsuranceList = (insuranceList: any[]) => {
    //Insurance list is rearranged after edit so, sorting by id.
    if (insuranceList?.length <= 0) {
        return [];
    }
    return insuranceList?.sort((a, b) => {
        return a?.id - b?.id;
    });
}

function getAlphanumericArray(): string[] {
    const alphabet = Array.from({length: 26}, (_, i) => String.fromCharCode(97 + i)); // 'a' to 'z'
    const digits = Array.from({length: 10}, (_, i) => i.toString()); // '0' to '9'
    return [...alphabet, ...digits];
}

function getStructuredSystemConfig(systemConfig: SystemConfigTypes | undefined) {
    return {
        expandLogo: systemConfig?.logo?.url || ImageConfig.sdiLogo,
        collapseLogo: systemConfig?.miniLogo?.url || ImageConfig.sdiLogoSmall,
    };
}

function formatMedicationNames(medication: medicationObject) {
    const {
        brandName = '',
        genericName = '',
        dosageForm = ''
    } = medication;

    return {
        ...medication,
        brandName: capitalizeFirstLetter(brandName),
        genericName: capitalizeFirstLetter(genericName),
        dosageForm: capitalizeFirstLetter(dosageForm)
    } as medicationObject;
}

function changeColorOpacity(color: string, opacity: number): string {
    try {
        if (opacity < 0 || opacity > 1) {
            console.error("Opacity must be between 0 and 1.");
            return color;
        }

        // Handle hex color (could be 3, 4, 6, 8 characters)
        if (color.startsWith("#")) {
            let hex = color.replace("#", "");

            // Handle 3-character or 4-character hex: expand to 6 or 8 characters
            if (hex.length === 3 || hex.length === 4) {
                hex = hex.split("").map(c => c + c).join("");  // Expand shorthand hex
            }

            // Validate the length (must be 6 or 8 characters after expansion)
            if (hex.length !== 6 && hex.length !== 8) {
                console.error("Invalid hex color format.");
                return color;
            }

            // Extract RGB components
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);

            // Extract alpha if present (last 2 characters of 8-character hex)
            const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : opacity;

            // Return rgba color with the specified opacity
            return `rgba(${r}, ${g}, ${b}, ${a * opacity})`;
        }

        // Handle rgba color
        if (color.startsWith("rgba")) {
            const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*(\d*\.?\d+)?\)/);
            if (rgbaMatch) {
                const [, r, g, b] = rgbaMatch;
                return `rgba(${r}, ${g}, ${b}, ${opacity})`;
            } else {
                console.error("Invalid rgba color format.");
                return color;
            }
        }

        // Handle rgb color
        if (color.startsWith("rgb")) {
            const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (rgbMatch) {
                const [, r, g, b] = rgbMatch;
                return `rgba(${r}, ${g}, ${b}, ${opacity})`;
            } else {
                console.error("Invalid rgb color format.");
                return color;
            }
        }

        console.error("Unsupported color format. Use hex, rgb, or rgba.");
        return color;
    } catch (error) {
        console.error("Error while changing color opacity:", error);
        return color;
    }
}

function getValueOrFallback(value: unknown, fallback: string = "N/A"): string | number {
    if (typeof value === "number") {
        return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        return value;
    }

    return fallback;
}

const physicianWithPrefix = (physician: any) => {
    return `${capitalizeFirstLetter(physician?.prefix) || ""} ${capitalizeFirstLetter(physician?.firstName)} ${capitalizeFirstLetter(physician?.middleName) || ""} ${capitalizeFirstLetter(physician?.lastName) || ""}`
}
const isValidLabRole = (role: string | undefined, labRoles: {
    title: string,
    code: LabRoleType
}[]): role is LabRoleType => {
    if (labRoles?.length > 0) {
        return labRoles?.some((labRole) => labRole.code === role);
    } else {
        return false;
    }
};

const getNormalizedFileType = (fileType: any) => {
    let type: any = fileType;
    if (type.includes('image')) {
        type = "image";
    } else if (type.includes('pdf')) {
        type = "pdf";
    } else if (type.includes('word')) {
        type = "word";
    } else if (type.includes('spreadsheet')) {
        type = "xls";
    } else if (type.includes('video')) {
        type = "video";
    } else {
        type = "application";
    }
    return type;
}

const changeNeedHelpPlacement=(changePlacement:boolean)=>{
    const helpButton = document.querySelector('.help-desk-component')
    if (changePlacement) {
        helpButton?.classList.add('mrg-bottom-60');
        // document.body.classList.add('help-desk-component');
    } else {
        // document.body.classList.remove('help-desk-component');
        helpButton?.classList.remove('mrg-bottom-60');
    }
}

const getCurrentInstanceName=(currentUser:any)=>{
    return  currentUser?.systemConfig?.instanceName||'AdvanzInnovation';
}


const CommonService = {
    CurrentDate,
    parseQueryString,
    handleErrors,
    openDialog,
    openConfirmationDialog,
    formatSizeUnits,
    getRandomID,
    getBytesInMB,
    getCancelToken,
    getPayloadFilterDates,
    getFormDataFromJSON,
    capitalizeFirstLetter,
    capitalize,
    kFormatter,
    getUUID,
    getLettersFromNumber,
    getRandomNumberBetweenRange,
    getMinsAndSecondsFromSeconds,
    convertDateFormat,
    downloadFile,
    generateBlobFileFromUrl,
    getFlatJsonFromNestedJSON,
    getFlatQuestionList,
    getArrayOfValuesOfOneKeyFromJSON,
    Capitalize,
    capitalizeFirstLetterOfEachWord,
    getMaskedMobileNumber,
    isSpecificModuleToHide,
    isReadOnlyFeature,
    isMutationFeature,
    checkAccess,
    formatDate,
    formatTime,
    convertTo12HourFormat,
    rgbaToHex,
    hexToRgba,
    codeToTitleConverter,
    capitalizeOnlyFirstLetterOfEachWord,
    getFullName,
    generateRuleFromRuleObject,
    extract24hTimeFromDate,
    generateNameFromNameObject,
    getFilteredUsersDropdownList,
    removeEmptyValuesFromObject,
    convertJsonToCsv,
    extractCollectionDevicesFromSampleType,
    sortPatientInsuranceList,
    getAlphanumericArray,
    getStructuredSystemConfig,
    formatMedicationNames,
    changeColorOpacity,
    getValueOrFallback,
    physicianWithPrefix,
    isValidLabRole,
    mapLabRoleToName,
    getNormalizedFileType,
    changeNeedHelpPlacement,
    getCurrentInstanceName,
    // createValidationsObject,
    // createYupSchema,

    _api: ApiService,
    _communications: Communications,
    _routeConfig: RouteConfigService,
    _alert: AlertService,
    _localStorage: LocalStorageService,
    _account: AccountService,
    _user: UserService,
    _lab: LabService,
    _staticData: StaticDataService,
    _accessioning: AccessioningService,
    _location: LocationService,
    _biomarker: BiomarkerService,
    _facility: FacilityService,
    _testOrder: TestOrderService,
    _test: TestService,
    _result: resultService,
    _patient: PatientService,
    _qrcode: QrCodeService,
    _barcode: BarcodeService,
    _systemSettings: SystemSettingsService,
    _dashboard: DashboardService,
    _accessLevel: AccessLevelService,
    _inventory: InventoryService,
    _labReportGeneratorService: LabReportGeneratorService,
    _ticket: TicketService,
    setQueryParam,
    setUrlSearchParams,
    mapFilterDataToQueryParams,
    getAppliedFiltersCount,
    mapLabTypeToName,
}
export default CommonService;
